/**
 * Walk every card lot eligible for image-URL-based exact lookup and refresh
 * its FMV in place. Throttled to be polite to the upstream API; cancellable.
 *
 * Used by:
 *   * Configs -> Data Management -> "Refresh all FMVs" button (manual)
 *   * Sign-in auto-sweep on the first load of each calendar day
 *     (lastFmvRefreshAt is empty, invalid, or from a previous date)
 *
 * Bulk pool singletons and cards without an Image URL are skipped - those
 * still get refreshed via the existing per-row refresh button which falls
 * back to the fuzzy picker.
 *
 * Sealed sweep: any sealed lot with a linked TCG Product ID is refreshed
 * from the daily TCGCSV snapshot table.
 */
import { DB } from "./db";
import { Settings } from "./prefs";
import { toast } from "./dom";
import { fmvFromImageUrl } from "./fmvByImage";
import { isBulkPoolRow } from "./bulk";
import { isGradedCard, isFmvLocked } from "./fmv";
import { getCurrentSealedPrice } from "./sealedTcgcsv";
import { loadConditionMultipliers, multiplierFor, conditionSourceSuffix } from "./conditionMultiplier";

export interface FmvRefreshProgress {
  done: number;
  total: number;
  updated: number;
  skipped: number;
  failed: number;
  currentName?: string;
}

export interface FmvRefreshToken {
  cancel: () => void;
  promise: Promise<FmvRefreshProgress>;
}

const THROTTLE_MS = 220;  // ~4.5 req/s - well under 50/s with an API key

/** Identify which card lots are candidates for the sweep.
 *  Graded slabs are excluded — image matching returns the RAW card price, which
 *  under-values a slab; those are refreshed manually via the per-row button.
 *  FMV-locked lots are excluded — the user priced them manually on purpose.
 *  Lots with a TCG Product ID are excluded — the SERVER reprices those nightly
 *  (revalue_inventory() via pg_cron) from the local catalog; the browser sweep
 *  only covers unlinked stragglers via the legacy image-URL API path. */
export function eligibleCardsForRefresh(): any[] {
  return DB.table("cards").filter(r =>
    !isBulkPoolRow(r) && !isGradedCard(r) && !isFmvLocked(r)
    && !(Number(r["TCG Product ID"]) > 0)
    && !!(r["Image URL"] || "").trim() && (Number(r["Qty"]) || 0) > 0
  );
}

/**
 * One-time heal: a fully sold/traded-out lot (Qty <= 0) holds no inventory and
 * must show Current FMV 0. A prior bug repriced such lots to a phantom unit
 * price, inflating totals — this zeroes them out. Idempotent; returns the count
 * corrected. Bulk pool singletons are skipped.
 */
export function zeroEmptyLotFMV(): number {
  let fixed = 0;
  for (const r of DB.table("cards")) {
    if (isBulkPoolRow(r)) continue;
    if ((Number(r["Qty"]) || 0) <= 0 && (Number(r["Current FMV"]) || 0) !== 0) {
      DB.update("cards", "Card Lot ID", r["Card Lot ID"], { "Current FMV": 0, "Unrealized G/L": -(Number(r["Total Basis"]) || 0) });
      fixed++;
    }
  }
  for (const r of DB.table("sealed")) {
    if ((Number(r["Qty"]) || 0) <= 0 && (Number(r["Current FMV"]) || 0) !== 0) {
      DB.update("sealed", "Lot ID", r["Lot ID"], { "Current FMV": 0, "Unrealized G/L": -(Number(r["Total Basis"]) || 0) });
      fixed++;
    }
  }
  return fixed;
}

export function startFmvRefreshAll(
  onProgress: (p: FmvRefreshProgress) => void,
): FmvRefreshToken {
  let cancelled = false;
  const cancel = () => { cancelled = true; };

  const promise = (async (): Promise<FmvRefreshProgress> => {
    const rows = eligibleCardsForRefresh();
    const progress: FmvRefreshProgress = {
      done: 0, total: rows.length, updated: 0, skipped: 0, failed: 0,
    };
    onProgress({ ...progress });

    // Condition multipliers must be in hand BEFORE any write. Without them a
    // played card would be repriced at its Near Mint price, overstating the lot
    // and fighting the nightly job. Abort the whole sweep instead — it is a
    // daily convenience and skipping a run costs nothing.
    if (!(await loadConditionMultipliers())) {
      console.warn("[FMV refresh] condition multipliers unavailable — sweep skipped");
      toast("FMV sweep skipped — couldn't load condition multipliers.");
      progress.skipped = rows.length;
      progress.done = rows.length;
      onProgress({ ...progress });
      return progress;
    }

    for (const row of rows) {
      if (cancelled) break;
      progress.currentName = row["Card Name"] || "(unnamed)";
      onProgress({ ...progress });
      try {
        const result = await fmvFromImageUrl(row);
        const grade = row["Condition/Grade"];
        const mult = multiplierFor(grade);
        if (result && mult != null) {
          const qty = Number(row["Qty"]) || 0;
          // Multiply the SOURCE price, never the stored FMV — otherwise the
          // discount compounds a little more every sweep. Same rule as the SQL.
          const totalFMV = +(result.unit * mult * qty).toFixed(2);
          const basis = Number(row["Total Basis"]) || 0;
          const baseSource = row["Game"] === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer";
          DB.update("cards", "Card Lot ID", row["Card Lot ID"], {
            "Current FMV": totalFMV,
            "Unrealized G/L": +(totalFMV - basis).toFixed(2),
            "Valuation Source": baseSource + conditionSourceSuffix(grade, mult),
          });
          progress.updated++;
        } else {
          progress.skipped++;
        }
      } catch (e: any) {
        console.error(`[FMV refresh] ${row["Card Name"]}:`, e);
        progress.failed++;
      }
      progress.done++;
      onProgress({ ...progress });
      if (!cancelled && progress.done < rows.length) {
        await new Promise<void>(res => setTimeout(res, THROTTLE_MS));
      }
    }

    progress.currentName = undefined;
    onProgress({ ...progress });

    // Stamp the timestamp on completion (even if cancelled - partial success counts).
    Settings.set({ lastFmvRefreshAt: new Date().toISOString() });
    return progress;
  })();

  return { cancel, promise };
}

/** Returns true when the last sweep happened on a different calendar day
 *  than today (i.e. fire on the first load of each new day) - or when no
 *  sweep has ever run on this account. Uses the local timezone of the
 *  browser, since the user's idea of "today" is what matters. */
export function shouldAutoRefresh(): boolean {
  const last = Settings.get().lastFmvRefreshAt;
  if (!last) return true;
  const lastDate = new Date(last);
  if (isNaN(lastDate.getTime())) return true;
  const today = new Date();
  return lastDate.toDateString() !== today.toDateString();
}

/** Kick off a silent background sweep. Just toasts at start and end.
 *  Runs cards (network) first, then sealed (local DB) once cards complete. */
export function runAutoFmvSweep(): void {
  const cardRows = eligibleCardsForRefresh();
  const sealedRows = eligibleSealedForRefresh();
  const total = cardRows.length + sealedRows.length;
  if (!total) {
    Settings.set({ lastFmvRefreshAt: new Date().toISOString() });
    return;
  }
  toast(`Refreshing ${total} FMV(s) in background...`);
  const { promise } = startFmvRefreshAll(() => { /* silent */ });
  promise.then(p => {
    if (sealedRows.length) {
      const sealedTok = startSealedFmvRefresh(() => { /* silent */ });
      sealedTok.promise.then(sp => {
        toast(`FMV sweep done - cards: ${p.updated}/${p.skipped}/${p.failed}, sealed: ${sp.updated}/${sp.skipped}/${sp.failed} (upd/skip/fail)`);
        (window as any).render?.();
      });
    } else {
      toast(`FMV sweep done - ${p.updated} updated, ${p.skipped} skipped, ${p.failed} failed`);
      (window as any).render?.();
    }
  });
}

/* ============================================================
   Sealed sweep - uses the TCGCSV-linked productId on each lot.
   Lots without a linked productId are skipped (the user needs
   to link them via the Sealed product picker first).
   ============================================================ */

/** Sealed lots eligible for the catalog-driven sweep.
 *  FMV-locked lots are excluded — the user priced them manually on purpose. */
export function eligibleSealedForRefresh(): any[] {
  return DB.table("sealed").filter(r => {
    const pid = Number(r["TCG Product ID"]) || 0;
    return pid > 0 && !isFmvLocked(r) && (Number(r["Qty"]) || 0) > 0;
  });
}

export function startSealedFmvRefresh(
  onProgress: (p: FmvRefreshProgress) => void,
): FmvRefreshToken {
  let cancelled = false;
  const cancel = () => { cancelled = true; };

  const promise = (async (): Promise<FmvRefreshProgress> => {
    const rows = eligibleSealedForRefresh();
    const progress: FmvRefreshProgress = {
      done: 0, total: rows.length, updated: 0, skipped: 0, failed: 0,
    };
    onProgress({ ...progress });

    for (const row of rows) {
      if (cancelled) break;
      progress.currentName = row["Product"] || `Lot ${row["Lot ID"]}`;
      onProgress({ ...progress });
      try {
        const pid = Number(row["TCG Product ID"]) || 0;
        const p = await getCurrentSealedPrice(pid);
        const unit = p.market ?? p.mid ?? p.low ?? null;
        if (unit != null) {
          const qty = Number(row["Qty"]) || 0;
          const totalFMV = +(unit * qty).toFixed(2);
          const basis = Number(row["Total Basis"]) || 0;
          DB.update("sealed", "Lot ID", row["Lot ID"], {
            "Current FMV": totalFMV,
            "Unrealized G/L": +(totalFMV - basis).toFixed(2),
            "Valuation Source": "TCGplayer Market",
          });
          progress.updated++;
        } else {
          progress.skipped++;
        }
      } catch (e: any) {
        console.error(`[Sealed FMV refresh] ${row["Product"]}:`, e);
        progress.failed++;
      }
      progress.done++;
      onProgress({ ...progress });
      // Sealed sweep is local DB-side - no network throttling needed, but a
      // tiny delay keeps the progress UI legible.
      if (!cancelled && progress.done < rows.length) {
        await new Promise<void>(res => setTimeout(res, 40));
      }
    }

    progress.currentName = undefined;
    onProgress({ ...progress });
    return progress;
  })();

  return { cancel, promise };
}
